import { Component, OnInit, ViewChild, ViewContainerRef } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import {
  ActionSheetButton,
  ActionSheetController,
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
  IonRefresher,
  IonRefresherContent,
  IonTitle,
  IonToolbar,
  Platform,
  ToastController,
} from '@ionic/angular/standalone';
import { ApiInit } from '../../utils/api-init';
import { FeedService } from '../../services/feed.service';
import { Feed } from '../../interfaces/feed';
import { Subscription } from 'rxjs';
import { UpdateService } from 'src/app/services/update.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CardType } from 'src/app/enums/card-type';
import { DeviceService } from '../../services/device.service';
import { Update } from 'src/app/interfaces/update';
import { Event } from 'src/app/interfaces/event';
import { VideoService } from '../../services/video.service';
import { ProjectService } from 'src/app/services/project.service';
import { ApiService } from 'src/app/services/api.service';
import { CurrentWhiteLabelApplication } from 'src/app/utils/current-white-label-application';
import { OnboardingService } from '../../services/onboarding.service';
import { AnalyticsService } from 'src/app/services/analytics.service';
import { EventService } from 'src/app/services/event.service';
import { AnalyticsType } from 'src/app/enums/analytics-type';
import { NavigationEnd, Router } from '@angular/router';
import { NgxSmartBannerService } from '@netcreaties/ngx-smart-banner';
import { LoaderComponent } from '../../components/loader/loader.component';
import { ProjectSuggestionSliderComponent } from '../../components/project-suggestion-slider/project-suggestion-slider.component';
import { FeedItemComponent } from '../../components/feed-item/feed-item.component';
import { CardComponent } from '../../components/cards/card/card.component';
import { InlineSVGModule } from 'ng-inline-svg-2';
import { NgFor, NgIf } from '@angular/common';
import { Project } from '../../interfaces/project';
import { Hydra } from '../../interfaces/hydra';
import { delay } from '../../utils/delay';

@Component({
  selector: 'app-feed',
  templateUrl: './feed.page.html',
  standalone: true,
  imports: [
    NgIf,
    InlineSVGModule,
    NgFor,
    CardComponent,
    FeedItemComponent,
    ProjectSuggestionSliderComponent,
    LoaderComponent,
    TranslateModule,
    IonContent,
    IonRefresher,
    IonRefresherContent,
    IonInfiniteScroll,
    IonInfiniteScrollContent,
    IonHeader,
    IonToolbar,
    IonTitle,
    IonButtons,
    IonButton,
    IonIcon,
  ],
})
export class FeedPage implements OnInit {
  feed: Feed[];
  page: number = 1;
  perPage: number = 7;
  totalItems: number;
  loadingFinished: boolean = false;
  loading: boolean = true;
  categories: string[] = [];
  activeCategory: string;
  paramsSub: Subscription;
  shouldReload: boolean = false;
  isAuthenticated: boolean;
  isProjectApp: boolean = false;
  CardType = CardType;
  notificationCount: number = 0;
  updateDeviceData: any = {};
  eventCards: any;
  @ViewChild('contentRef', { read: ViewContainerRef })
  contentRef: ViewContainerRef;

  private seenUpdates: Array<Feed> = [];
  private routerSubscription: Subscription;
  private performanceStartTime: number = 0;

  constructor(
    private feedService: FeedService,
    private actionSheetCtrl: ActionSheetController,
    private updateService: UpdateService,
    private translateService: TranslateService,
    private platform: Platform,
    private deviceService: DeviceService,
    private analyticsService: AnalyticsService,
    private videoService: VideoService,
    private apiInit: ApiInit,
    private apiService: ApiService,
    private projectService: ProjectService,
    private app: CurrentWhiteLabelApplication,
    private onboardingService: OnboardingService,
    private eventService: EventService,
    private router: Router,
    private ngxSmartBannerService: NgxSmartBannerService,
    private toastCtrl: ToastController
  ) {
    this.performanceStartTime = performance.now();
    this.isProjectApp = this.app.isProjectApp();

    this.routerSubscription = this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.ionViewWillEnter();
      }
    });
  }

  ngOnDestroy() {
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }

  ngAfterViewInit() {
    this.apiInit.watch(async () => {
      if (this.platform.is('mobileweb')) {
        this.initSmartBanner();
      }
    });
  }

  async ngOnInit() {
    this.apiInit.watch(async () => {
      this.loadFeed(null, true).then(() => {
        this.analyticsService.logPerformance(
          AnalyticsType.FEED_LOADED,
          this.performanceStartTime
        );
      });
      try {
        this.categories = await this.updateService.getUpdateCategories();
      } catch (error) {
        // ignore
      }
    });

    this.apiService.userLoggedIn.subscribe(() => {
      this.shouldReload = true;
    });

    this.apiService.userLoggedOut.subscribe(() => {
      this.shouldReload = true;
    });

    this.projectService.starChanged$.subscribe(() => {
      this.shouldReload = true;
    });

    this.deviceService.addressUpdated$.subscribe(() => {
      this.shouldReload = true;
    });

    this.onboardingService.obnoardingFinished$.subscribe(() => {
      this.handleReload();
    });
  }

  initSmartBanner() {
    this.ngxSmartBannerService.initialize({
      viewContainerRef: this.contentRef,
      title: this.app.getName(),
      price: this.translateService.instant('banner.free') + ' ',
      buttonLabel: this.translateService.instant('banner.view'),
      hideRating: true,
      daysHidden: 30,
      daysReminder: 30,
      author: this.app.getCustomer(),
      priceSuffix: {
        ios: this.translateService.instant('banner.ios'),
        android: this.translateService.instant('banner.android'),
      },
      icon: {
        ios: this.app.getIconThumbnails().full,
        android: this.app.getIconThumbnails().full,
      },
      buttonUrl: {
        ios: 'https://apps.apple.com/app/' + this.app.getIOSAppStoreId(),
        android:
          'https://play.google.com/store/apps/details?id=' +
          this.app.getBundleIdentifer(),
      },
    });
  }

  async getAllEvents() {
    const eventCards: { project: Project; events: Event[] }[] = [];
    const events = await this.eventService.getAllEvents();
    events.forEach((event: Event) => {
      const projectIndex = eventCards.findIndex(
        (x) => x.project.slug == event.updates[0].project.slug
      );
      if (projectIndex > -1) {
        if (eventCards[projectIndex].events.length)
          eventCards[projectIndex].events.push(event);
        else eventCards[projectIndex].events = [event];
      } else {
        eventCards.push({
          project: event.updates[0].project,
          events: [event],
        });
      }
    });

    this.eventCards = eventCards;
  }

  onItemVisible(item: Feed) {
    const isNew = !this.seenUpdates.includes(item);
    if (isNew) {
      this.analyticsService.logEvent(
        AnalyticsType.UPDATE_VIEW,
        {
          update_id: item.projectUpdate.id,
        },
        item.project
      );
      this.seenUpdates = [...this.seenUpdates, ...[item]];
    }
  }

  onScrollEnd() {
    this.videoService.checkVideos();
  }

  private handleReload() {
    this.page = 1;
    this.loadFeed(null, true);
    this.shouldReload = false;
  }

  ionViewWillEnter() {
    if (this.shouldReload) {
      this.handleReload();
    }
    if (!this.isProjectApp) {
      this.onboardingService.checkAndShowOnboarding();
    }
  }

  removeItem(id: any) {
    const indexToRemove = this.feed.findIndex((item) => item.id === id);

    if (indexToRemove !== -1) {
      // Remove the object from the array
      this.feed.splice(indexToRemove, 1);
    }
  }

  async loadFeed(event = null, retry: boolean = false) {
    this.loading = true;

    const maxAttempts = 3;
    let attempts = 0;
    let response: Hydra<Feed>;

    while (attempts < maxAttempts) {
      try {
        response = await this.feedService.getFeed(this.setUrlParams());

        if (retry && response.totalItems === 0 && attempts < maxAttempts - 1) {
          attempts++;
          await delay(1000); // wait before retry
          continue;
        }

        break; // success
      } catch (error) {
        attempts++;
        if (attempts >= maxAttempts) {
          console.error('Failed to load feed after multiple attempts', error);
          this.loading = false;
          return;
        }
        await delay(1000); // wait before retry
      }
    }

    if (response) {
      if (!this.feed?.length || this.page === 1) {
        this.feed = response.member;
      } else {
        this.feed = this.feed.concat(response.member);
      }

      this.totalItems = response.totalItems;
      this.page++;
      this.loadingFinished = true;
      this.loading = false;
      this.getDeviceData();
      this.getAllEvents();
      this.shouldReload = false;

      if (event) event.target.complete();
    }
  }

  async getDeviceData() {
    let updates: Update[] = [];
    this.feed.forEach((item) => {
      if (item.type === CardType.UPDATE) {
        updates.push(item.projectUpdate);
      }
    });

    try {
      const response = await this.deviceService.getData(
        new HttpParams().set(
          'update_ids',
          updates.map(({ id }) => id).join(',')
        )
      );
      this.updateDeviceData = response.updates;
    } catch (error) {}
  }

  updateFilter(category: string) {
    this.page = 1;
    this.activeCategory = category;
    this.loadFeed();
  }

  openNotifications() {
    this.router.navigate(['notifications']);
  }

  async openFilter() {
    let buttons: ActionSheetButton[] = [
      {
        text: this.translateService.instant('feed.filter.all'),
        cssClass: !this.activeCategory ? 'activeBtn' : 'filterBtn',
        handler: () => {
          this.updateFilter(null);
        },
      },
    ];

    Object.keys(this.categories).forEach((categoryId) => {
      buttons.push({
        text: this.categories[categoryId],
        cssClass: this.activeCategory == categoryId ? 'activeBtn' : 'filterBtn',
        handler: () => {
          this.updateFilter(categoryId);
        },
      });
    });

    buttons.push({
      text: this.translateService.instant('feed.filter.cancel'),
      role: 'cancel',
      cssClass: 'cancelBtn',
    });

    const actionSheet = await this.actionSheetCtrl.create({
      header: this.translateService.instant('feed.filter'),
      cssClass: 'custom-actionsheet',
      buttons: buttons,
    });

    await actionSheet.present();
  }

  setUrlParams() {
    let params = new HttpParams()
      .set('page', this.page.toString())
      .set(
        'perPage',
        this.platform.is('desktop')
          ? (this.perPage * 3).toString()
          : this.perPage.toString()
      );
    if (this.activeCategory)
      params = params.set('category', this.activeCategory);
    return params;
  }

  handleRefresh(event) {
    this.page = 1;
    this.loadFeed(event);
  }
}
